/**
 * @module utils/resetDimensionPercentageOnDataSourceSwitch
 * @description 切换数据源时，重置 `save_column_infos.chartsCfgSave[*].customizeSetting[*].dimension_percentage`
 *
 * ## 背景
 *
 * 表格「维度占比」（`dimension_percentage`）按物理列名引用 metric / group_by.column，
 * 一旦数据源（table）切换，旧列引用必然全部失效。
 * QB 联动管道 `dimensionPercentagePruneHandler` 只能裁剪「仍能匹配到新 dataTables 的列」；
 * 切换数据源后旧 table 的列在新表上根本不存在，无法通过 column_name 匹配裁剪，
 * 必须一次性把整段 `dimension_percentage` 重置为默认空态。
 *
 * ## 调用时机
 *
 * 仅在「用户主动切换数据源并确认」时调用，对应路径：
 * `HeaderData/dataSourceRelation.tsx` 的 `getSuccess`（simple / personal 两个 Tab 各一处）。
 *
 * **不在以下路径调用**（避免误清）：
 * - `model.getTaskDetail`（打开已保存图表，dimension_percentage 需要原样恢复）
 * - 自定义列 rename / delete（由 `dimensionPercentagePruneHandler` 走 QB 联动）
 *
 * ## 行为
 *
 * - 对 `chartsCfgSave` 的所有图表类型（Table / LineChart / BarChart / ...），
 *   在 `customizeSetting[ConfigChartType[chartType]]` 上把 `dimension_percentage`
 *   重置为「`{ metrics: [] }`」（与 `STRUCTURED_SETTING_DEFAULTS.dimension_percentage` 形态一致）
 * - 不存在的层（无 `customizeSetting` / 无对应 storage key / 已无 `dimension_percentage`）→ 跳过，不写脏数据
 * - 输入为空 / 非对象 → 原样返回
 * - 不可变：不修改原 `save_column_infos`；仅在确实发生重置的层产生新引用
 */
import { ConfigChartType, type TChartType } from '@dbl/core/chart';

/** 关闭后的空 metrics 列表形态（与 `DIMENSION_PERCENTAGE_DEFAULT` 字段形态一致） */
type TResetDimensionPercentage = { metrics: never[]; };
export const RESET_DIMENSION_PERCENTAGE_VALUE: TResetDimensionPercentage = {
  metrics: [],
};

/** 兼容层：尽可能放宽类型，避免对 `save_column_infos` 做整体类型对齐 */
type AnyRecord = Record<string, any>;

/** 判断是否「有非空 metrics 列表」（已配置过维度占比） */
function hasConfiguredDimensionPercentage(slot: unknown): boolean {
  if (!slot || typeof slot !== 'object') return false;
  const { metrics } = slot as { metrics?: unknown; };
  return Array.isArray(metrics) && metrics.length > 0;
}

/**
 * 切换数据源时，重置 `save_column_infos.chartsCfgSave[*].customizeSetting[*].dimension_percentage`。
 *
 * - 输入为空 / 非对象 → 原样返回
 * - `chartsCfgSave` 不存在或为空对象 → 原样返回
 * - 仅对「**有 dimension_percentage 配置**」的层做深拷贝重置，未配置过的层不写脏数据
 *
 * @example
 * const next = resetDimensionPercentageOnDataSourceSwitch(saveColumnInfos);
 * // next.chartsCfgSave.Table.customizeSetting.table.dimension_percentage === { metrics: [] }
 */
export function resetDimensionPercentageOnDataSourceSwitch<T = unknown>(
  saveColumnInfos: T,
): T {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as AnyRecord;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, AnyRecord>
    | undefined;
  if (!chartsCfgSave || typeof chartsCfgSave !== 'object') {
    return saveColumnInfos;
  }

  let nextChartsCfgSave: Record<string, AnyRecord> | null = null;

  // 遍历所有 chartType（key 如 'Table' / 'LineChart' / ...）
  for (const chartType of Object.keys(chartsCfgSave) as TChartType[]) {
    const storageKey = ConfigChartType[chartType];
    if (!storageKey) continue;

    const wrapper = chartsCfgSave[chartType];
    if (!wrapper || typeof wrapper !== 'object') continue;
    const customizeSetting = wrapper.customizeSetting as
      | Record<string, AnyRecord>
      | undefined;
    if (!customizeSetting || typeof customizeSetting !== 'object') continue;

    const inner = customizeSetting[storageKey];
    if (!inner || typeof inner !== 'object') continue;

    // 仅在「确实配置过 dimension_percentage」时重置
    // - 已存在但 metrics 已是空数组：保持原样（避免无意义引用变动）
    // - 不存在：保持不存在（避免给未配置过的层写入键）
    if (!hasConfiguredDimensionPercentage(inner.dimension_percentage)) {
      continue;
    }

    if (!nextChartsCfgSave) {
      nextChartsCfgSave = { ...chartsCfgSave };
    }
    const nextInner: AnyRecord = { ...inner, dimension_percentage: { ...RESET_DIMENSION_PERCENTAGE_VALUE } };
    nextChartsCfgSave[chartType] = {
      ...wrapper,
      customizeSetting: {
        ...customizeSetting,
        [storageKey]: nextInner,
      },
    };
  }

  if (!nextChartsCfgSave) {
    return saveColumnInfos;
  }
  const nextRoot: AnyRecord = { ...root, chartsCfgSave: nextChartsCfgSave };
  return nextRoot as T;
}
